import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  Stack,
  Img,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormErrorMessage,
  Button,
  Link,
  Text,
  useToast,
  useBreakpointValue,
} from '@chakra-ui/react'
import { FiUser, FiLock } from 'react-icons/fi'
import Swal from 'sweetalert2'

import cmsBackground from '../../assets/photo/cms-cidade-inteligente.jpeg'
import logo from '../../assets/svg/logo_gier.svg'
import logoSantos from '../../assets/photo/prefeituraSantos.png';
import logoOsasco from '../../assets/photo/prefeituraOsascoNovo.png';
import logocms from '../../assets/svg/logo_cidadeinteligente.svg'
import Footer from '../../components/Footer'
import { useAuth } from '../../contexts/AuthContext'
import * as api from '../../api/auth'
import * as apiImagem from '../../api/imagemPerfil'
import { autenticarCpf } from '../../api/usuario'

const Login: React.FC = () => {
  const [formValues, setFormValues] = useState({
    userName: { value: '', error: false },
    password: { value: '', error: false },
  })
  const [loading, setLoading] = useState(false)
  const toast = useToast()
  const navigate = useNavigate()
  const { login } = useAuth()

  const isLogoGier = ['cms-demo.acto.net.br', 'cms-dev.acto.net.br'].includes(window.location.hostname);

  const isLogoSantos = ['cms-santos.acto.net.br'].includes(window.location.hostname);

  const logoSrc = isLogoGier ? logo : isLogoSantos ? logoSantos : logoOsasco;

  const handleFieldChange = (
    field: 'userName' | 'password',
    value: string
  ) => {
    setFormValues({
      ...formValues,
      [field]: { value, error: false },
    })
  }

  const isValidateForm = () => {
    let valid = true
    const next = { ...formValues }

    if (!next.userName.value.trim()) {
      next.userName.error = true
      valid = false
    }
    if (!next.password.value.trim()) {
      next.password.error = true
      valid = false
    }

    setFormValues(next)
    return valid
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValidateForm()) return

    setLoading(true)
    try {
      const res = await login(
        'password',
        0,
        formValues.userName.value,
        formValues.password.value
      )

      if (res.error) {
        toast({
          title: 'Login e/ou senha inválidos',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return
      }

      if (res && res.CODIGO_USUARIO) {
        const data = await autenticarCpf(res.CODIGO_USUARIO)
        localStorage.setItem('dadosUsuario', JSON.stringify(data))

        const acessoMenuData = await api.obterAcessoMenu(
          data.codPerfilAcesso
        )
        if (!acessoMenuData?.length) {
          await Swal.fire({
            icon: 'warning',
            title: 'Alerta!',
            html: `Seu perfil não possui permissões associadas.<br/>Você não poderá utilizar a plataforma.`,
            confirmButtonColor: '#1789FC',
            confirmButtonText: 'OK',
          })
          localStorage.removeItem('permissaoAcesso')
          navigate('/login')
          return
        }

        localStorage.setItem(
          'permissaoAcesso',
          JSON.stringify(acessoMenuData)
        )

        const imagemData = await apiImagem.obterImagem(
          Number(data.seqUsuario)
        )
        if (imagemData.imagem) {
          localStorage.setItem(
            'dadosUsuario',
            JSON.stringify({ ...data, imagemPerfil: imagemData.imagem })
          )
        }

        navigate('/cms/inicio')
      }

    } catch (err) {
      toast({
        title: 'Erro no login',
        description: 'Verifique suas credenciais e tente novamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }

  const cardWidth = useBreakpointValue({
    base: '90%',
    sm: '360px',
    md: '400px',
  })

  return (
    <>
      <Container
        overflowX="auto"
        color="#000"
        bgImage={`linear-gradient(rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5)), url(${cmsBackground})`}
        bgSize="contain"
        bgRepeat="no-repeat"
        bgPos="center"
        bgColor="#fff"
        display="flex"
        alignItems="center"
        justifyContent="center"
        w="100%"
        maxWidth="100%"
        maxHeight="100%"
        height="100vh"
        p="0"
        m="0"
      >
        <Box
          w={cardWidth}
          bg="whiteAlpha.900"
          backdropFilter="blur(6px)"
          borderRadius="md"
          boxShadow="lg"
          p={{ base: 6, sm: 8 }}
        >
          <form onSubmit={handleSubmit}>
            <Stack spacing={6}>
              <Img src={logoSrc} w="180px" mx="auto" />

              <Text
                fontSize="lg"
                textAlign="center"
                fontWeight="semibold"
              >
                Sistema de Gestão de Conteúdo
              </Text>

              <FormControl isInvalid={formValues.userName.error}>
                <InputGroup>
                  <InputLeftElement
                    pointerEvents="none"
                    color="gray.400"
                  >
                    <FiUser />
                  </InputLeftElement>
                  <Input
                    placeholder="Usuário"
                    value={formValues.userName.value}
                    onChange={(e) =>
                      handleFieldChange('userName', e.target.value)
                    }
                    focusBorderColor="blue.400"
                  />
                </InputGroup>
                <FormErrorMessage>
                  Campo obrigatório
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={formValues.password.error}>
                <InputGroup>
                  <InputLeftElement
                    pointerEvents="none"
                    color="gray.400"
                  >
                    <FiLock />
                  </InputLeftElement>
                  <Input
                    type="password"
                    placeholder="Senha"
                    value={formValues.password.value}
                    onChange={(e) =>
                      handleFieldChange('password', e.target.value)
                    }
                    focusBorderColor="blue.400"
                  />
                </InputGroup>
                <FormErrorMessage>
                  Campo obrigatório
                </FormErrorMessage>
              </FormControl>

              <Button
                type="submit"
                w="full"
                color={'#fff'}
                bgColor={"#407494"}
                _hover={{ bgColor: "#4485ac" }}
                _focus={{ bgColor: "#407494", boxShadow: "none" }}
                isLoading={loading}
                loadingText="Entrando..."
              >
                Acessar
              </Button>

              {/* <Link
                color="blue.600"
                fontSize="sm"
                textAlign="right"
                onClick={() => {
                }}
              >
                Esqueci minha senha
              </Link> */}
            </Stack>
          </form>
        </Box>
      </Container>

      <Footer />
    </>
  )
}

export default Login
